import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Copy, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useTelecomAccess } from "@/hooks/useTelecomAccess";
import { SectorNoHabilitadoPanel } from "@/components/SectorNoHabilitadoPanel";
import {
  Alert,
  Button,
  Card,
  DataField,
  FormField,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Textarea,
} from "@/components/ui";

interface CertificadoDetalle {
  id: string;
  numCertificado: string;
  fechaEmision: string;
  titularRazonSocial: string | null;
  productoNombre: string;
  productoModelo: string;
  marca: string;
}

const COSTO_UNITARIO = 187;
const IVA = 0.16;

const FORMATOS = [
  { codigo: "FISICA", label: "Física (recoger en oficinas NPMX)" },
  { codigo: "DIGITAL", label: "Digital (PDF firmado con FIEL)" },
  { codigo: "AMBOS", label: "Ambos" },
];

const MOTIVOS = [
  "Requerimiento de cliente / autoridad",
  "Pérdida o extravío del original",
  "Daño del original",
  "Otro",
];

/**
 * Pantalla dedicada de Copia Fiel de un Certificado (#19), mockup
 * 32-Copia_Fiel_Certificado.html. Se llega desde la Selección de Certificado
 * con ?cert=<certificadoId>. El titular y los datos de producto vienen del
 * certificado de origen; aquí sólo se capturan cantidad, formato y motivo.
 */
export function CopiaFielAltaPage() {
  const access = useTelecomAccess();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const certId = params.get("cert") ?? "";

  const [cantidad, setCantidad] = useState(1);
  const [formato, setFormato] = useState("FISICA");
  const [motivo, setMotivo] = useState(MOTIVOS[0]);
  const [comentarios, setComentarios] = useState("");
  const [error, setError] = useState<string | null>(null);

  const qCert = useQuery<CertificadoDetalle>({
    queryKey: ["telecom-certificado", certId],
    queryFn: async () =>
      (await api.get<CertificadoDetalle>(`/api/sector/telecom/certificados/${certId}`)).data,
    enabled: access.allowed && !!certId,
  });

  // Cálculo conservado a propósito: la visualización del costo está oculta
  // por ajuste de UX (doc 202605291014). El campo y la lógica siguen vivos
  // para reactivarse cuando se decida exponerlos.
  const totalCosto = useMemo(() => {
    const subtotal = Math.max(1, Math.min(10, cantidad || 1)) * COSTO_UNITARIO;
    return { subtotal, iva: subtotal * IVA, total: subtotal * (1 + IVA) };
  }, [cantidad]);
  void totalCosto;

  const mutation = useMutation({
    mutationFn: async () => {
      const body = {
        certificadoBaseId: certId,
        cantidadCopias: cantidad,
        formatoEntrega: formato,
        motivo,
        comentarios: comentarios.trim() || null,
      };
      return (await api.post<{ id: string }>("/api/sector/telecom/solicitudes/copia-fiel", body)).data;
    },
    onSuccess: (d) => navigate(`/sector/telecom/solicitudes/${d.id}/success`),
    onError: (e: any) => {
      setError(e?.response?.data?.detail ?? e?.response?.data?.message ?? "No se pudo registrar la solicitud.");
    },
  });

  if (access.loading) {
    return (
      <div className="flex items-center justify-center p-12 text-sm text-fg-subtle">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Validando permisos…
      </div>
    );
  }
  if (!access.allowed) {
    return (
      <SectorNoHabilitadoPanel
        sectorNombre="Telecomunicaciones"
        sectorCodigo="TELECOM"
        titularRazonSocial={access.titularRazonSocial}
        sinContexto={access.sinContexto}
      />
    );
  }
  if (!certId) {
    return (
      <Alert variant="warning">
        Falta el certificado. Vuelve a la{" "}
        <Link to="/telecom/servicios/seleccionar-certificado?s=copia-fiel" className="font-medium underline">
          selección de certificado
        </Link>.
      </Alert>
    );
  }

  const cantidadValida = cantidad >= 1 && cantidad <= 10;

  return (
    <PageContainer width="form">
      <PageHeader
        backTo="/telecom/servicios/seleccionar-certificado?s=copia-fiel"
        backLabel="Cambiar certificado"
        icon={<Copy className="h-5 w-5" />}
        title="Copia Fiel de un Certificado"
        subtitle="Reimpresión certificada del certificado original, con la misma validez legal."
      />

      <Alert variant="info">
        Servicio rápido — emisión típica en 1 a 2 días hábiles.
      </Alert>

      <Card title="Certificado seleccionado">
        {qCert.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando certificado…</div>
        ) : qCert.error || !qCert.data ? (
          <div className="text-sm text-estado-warn">No se pudo cargar el certificado seleccionado.</div>
        ) : (
          <dl className="grid gap-4 sm:grid-cols-2">
            <DataField label="Núm. de Certificado" value={<span className="font-mono">{qCert.data.numCertificado}</span>} />
            <DataField label="Razón Social" value={qCert.data.titularRazonSocial ?? "—"} />
            <DataField label="Producto" value={`${qCert.data.productoNombre} ${qCert.data.productoModelo}`} />
            <DataField label="Fecha de expedición" value={formatDate(qCert.data.fechaEmision)} />
          </dl>
        )}
      </Card>

      <Card title="Detalles de la copia">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Cantidad de copias" required hint="Mínimo 1, máximo 10.">
            <Input
              type="number"
              min={1}
              max={10}
              value={cantidad}
              onChange={(e) => setCantidad(parseInt(e.target.value, 10) || 0)}
            />
          </FormField>
          <FormField label="Formato de entrega" required>
            <Select value={formato} onChange={(e) => setFormato(e.target.value)}>
              {FORMATOS.map((f) => (
                <option key={f.codigo} value={f.codigo}>{f.label}</option>
              ))}
            </Select>
          </FormField>
        </div>

        <FormField label="Motivo de la solicitud" className="mt-4">
          <Select value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            {MOTIVOS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </Select>
        </FormField>

        <FormField label="Comentarios adicionales" className="mt-4">
          <Textarea
            value={comentarios}
            onChange={(e) => setComentarios(e.target.value)}
            placeholder="Información adicional para el equipo de NPMX (opcional)."
          />
        </FormField>

        {/* Resumen de costo: oculto por ajuste de UX (doc 202605291014). */}
      </Card>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex justify-end gap-2">
        <Link to="/telecom/servicios/seleccionar-certificado?s=copia-fiel">
          <Button variant="secondary">Regresar</Button>
        </Link>
        <Button
          variant="success"
          icon={<Send className="h-4 w-4" />}
          loading={mutation.isPending}
          disabled={!cantidadValida || qCert.isLoading || !!qCert.error}
          onClick={() => { setError(null); mutation.mutate(); }}
        >
          Enviar solicitud
        </Button>
      </div>
    </PageContainer>
  );
}

function formatDate(s: string): string {
  try {
    return new Date(s).toLocaleDateString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return s;
  }
}
